/**
 * Uma linha de contato da aba Contatos (emergência e suporte): nome (negrito) +
 * funções (etiquetas, GUPM-202) abaixo; telefone (link se houver) e e-mail (mailto, quando presente)
 * empilhados à direita.
 *
 * C-75: com `onEditar` (só admin) ganha o lápis ao lado do telefone; o inativo
 * aparece riscado, com o selo "Inativo" (operador e RT nem recebem inativos).
 */
import type { ContatoGestao } from '@/services/datasources';
import { EtiquetasFuncao } from '@/ui/EtiquetasFuncao';
import { PencilIcon } from './PencilIcon';

interface Props {
  contato: ContatoGestao;
  onEditar?: (contato: ContatoGestao) => void;
  /** No diretório por empresa: selo 🚨 em quem também é de emergência (GUPM-202). */
  seloEmergencia?: boolean;
}

export default function LinhaContato({ contato, onEditar, seloEmergencia = false }: Props) {
  const inativo = !contato.is_active;
  return (
    <li className="flex items-center justify-between gap-3 border-t border-jf-border py-2">
      <div className={`min-w-0 ${inativo ? 'opacity-60' : ''}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs font-semibold ${inativo ? 'text-jf-dim line-through' : 'text-white'}`}>
            {contato.name}
          </span>
          {seloEmergencia && contato.is_emergencia && (
            <span role="img" title="Também na lista de emergência" aria-label="Contato de emergência" className="text-[10px]">
              🚨
            </span>
          )}
          {inativo && (
            <span className="rounded-full border border-jf-border px-1.5 py-0.5 text-[9px] font-bold uppercase text-jf-dim">
              Inativo
            </span>
          )}
        </div>
        {/* GUPM-202: cada função é uma etiqueta. */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <EtiquetasFuncao contato={contato} />
          {contato.disponibilidade && <span className="text-[10px] text-jf-dim">{contato.disponibilidade}</span>}
        </div>
      </div>
      <div className="flex min-w-0 flex-col items-end gap-0.5 text-xs">
        <div className="flex items-center gap-1.5">
          {contato.phone ? (
            <a href={`tel:${contato.phone}`} className="whitespace-nowrap font-bold text-jf-yellow no-underline">
              {contato.display}
            </a>
          ) : (
            <span className="whitespace-nowrap italic text-jf-dim">{contato.display}</span>
          )}
          {onEditar && (
            <button
              type="button"
              onClick={() => onEditar(contato)}
              aria-label={`Editar ${contato.name} — ${contato.role}`}
              title="Editar contato"
              className="rounded p-1 leading-none text-jf-dim hover:bg-jf-yellow/10 hover:text-jf-yellow focus-visible:text-jf-yellow"
            >
              <PencilIcon />
            </button>
          )}
        </div>
        {/* E-mail clicável (GUPM-203) — mostra o e-mail COMPLETO; em telas estreitas
            quebra por caractere (break-all) em vez de cortar. Nada exibido quando null. */}
        {contato.email && (
          <a
            href={`mailto:${contato.email}`}
            aria-label={`Enviar e-mail para ${contato.name}`}
            className="break-all text-right text-[11px] text-jf-muted no-underline hover:text-jf-yellow"
          >
            {contato.email}
          </a>
        )}
      </div>
    </li>
  );
}
